import React, { useState } from 'react';
import {
    Box,
    Modal,
    Typography,
    Stack,
    useMediaQuery,
    CircularProgress,
    Alert,
    TextField,
    IconButton,
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CloseIcon from '@mui/icons-material/Close';
import { ActionButton, CancelButton } from '@/components/Button';
import VerificationResultsDialog from './VerificationResultsDialog';
import { verificationService, VerificationResponse } from '@/services/verificationService';
import { useResponsiveToast } from '@/components/Toast';

interface VerificationDialogProps {
    open: boolean;
    onClose: () => void;
    onVerify: (data: { idNumber: string; firstName: string; surname: string }) => Promise<void>;
    initialData: {
        idNumber: string;
        firstName: string;
        surname: string;
        addressLine1: string;
        addressLine2?: string; // Optional
        postalCode: string; // Required
    };
    requestId: string; // The form/record ID to use as request_id
    indigentNumber: string;
    userId: string;
    isVerifying?: boolean;
}

const VerificationDialog: React.FC<VerificationDialogProps> = ({
    open,
    onClose,
    onVerify,
    initialData,
    requestId,
    indigentNumber,
    userId,
    isVerifying = false,
}) => {
    const isMobile = useMediaQuery('(max-width:600px)');
    const { error: showError } = useResponsiveToast();
    
    // State for form data
    const [formData, setFormData] = useState({
        idNumber: initialData.idNumber,
        firstName: initialData.firstName,
        surname: initialData.surname,
        addressLine1: initialData.addressLine1,
        addressLine2: initialData.addressLine2 || '',
        postalCode: initialData.postalCode,
    });
    
    // State to track if user has started editing
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showResultsDialog, setShowResultsDialog] = useState(false);
    const [verificationResults, setVerificationResults] = useState<VerificationResponse | null>(null);

    // Reset form data when dialog opens/closes
    React.useEffect(() => {
        if (open) {
            setFormData({
                idNumber: initialData.idNumber,
                firstName: initialData.firstName,
                surname: initialData.surname,
                addressLine1: initialData.addressLine1,
                addressLine2: initialData.addressLine2 || '',
                postalCode: initialData.postalCode,
            });
            setIsEditing(false);
            setShowResultsDialog(false);
            setVerificationResults(null);
        }
    }, [open, initialData]);

    const handleFieldChange = (field: keyof typeof formData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        
        // Mark as editing if any field is changed from initial value
        const hasChanges = 
            formData.idNumber !== initialData.idNumber ||
            formData.firstName !== initialData.firstName ||
            formData.surname !== initialData.surname ||
            formData.addressLine1 !== initialData.addressLine1 ||
            formData.addressLine2 !== (initialData.addressLine2 || '') ||
            formData.postalCode !== initialData.postalCode;
        
        setIsEditing(hasChanges || value !== (initialData[field as keyof typeof initialData] || ''));
    };

    // Check if all required fields are filled
    const isFormValid = () => {
        return (
            formData.idNumber.trim() !== '' &&
            formData.firstName.trim() !== '' &&
            formData.surname.trim() !== '' &&
            formData.addressLine1.trim() !== '' &&
            formData.postalCode.trim() !== ''
        );
    };

    const handleVerify = async () => {
        // Validate required fields before submitting
        if (!isFormValid()) {
            showError('Please fill in all required fields.');
            return;
        }

        setIsLoading(true);
        try {
            // Call verification API
            const verificationPayload = {
                request_id: requestId, // Use the form/record ID
                id_number: formData.idNumber,
                first_name: formData.firstName,
                surname: formData.surname,
                address_line1: formData.addressLine1,
                address_line2: formData.addressLine2 || undefined,
                postal_code: formData.postalCode,
            };

            const results = await verificationService.verifyIndigent(verificationPayload);
            
            // Set verification results and show results dialog
            setVerificationResults(results);
            setShowResultsDialog(true);
            
        } catch (error) {
            console.error('Verification failed:', error);
            showError('Verification failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        // Reset form data to initial values
        setFormData({
            idNumber: initialData.idNumber,
            firstName: initialData.firstName,
            surname: initialData.surname,
            addressLine1: initialData.addressLine1,
            addressLine2: initialData.addressLine2 || '',
            postalCode: initialData.postalCode,
        });
        setIsEditing(false);
        onClose();
    };

    const handleResultsDialogClose = () => {
        setShowResultsDialog(false);
        setVerificationResults(null);
    };

    const handleCloseAllDialogs = () => {
        setShowResultsDialog(false);
        setVerificationResults(null);
        onClose(); // Close the parent verification dialog too
    };

    const style = {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: '#fff',
        borderRadius: '32px',
        boxShadow: 24,
        px: isMobile ? 3 : 4,
        py: isMobile ? 4 : 5,
        width: isMobile ? '85%' : 500,
        maxHeight: '90vh',
        overflow: 'auto',
    };

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            height: 48,
            '& fieldset': {
                borderColor: '#E3E2E2',
            },
            '&:hover fieldset': {
                borderColor: '#132F5F',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#132F5F',
                borderWidth: 2,
            },
        },
        '& .MuiInputLabel-root': {
            fontFamily: 'Poppins, sans-serif',
            fontSize: '14px',
            '&.Mui-focused': {
                color: '#132F5F',
            },
        },
        '& .MuiInputBase-input': {
            fontFamily: 'Poppins, sans-serif',
            fontSize: '14px',
            color: '#132F5F',
        },
    };

    return (
        <>
            <Modal open={open && !showResultsDialog} onClose={handleCancel}>
                <Box sx={style}>
                    <Stack spacing={isMobile ? 3 : 4}>
                        {/* Title */}
                        <Box sx={{ position: 'relative' }}>
                            <Stack direction='row' justifyContent='center' alignItems='center' spacing={1}>
                                <VerifiedUserIcon sx={{ color: '#132F5F', fontSize: { xs: 24, sm: 28 } }} />
                                <Typography
                                    fontFamily='Poppins, sans-serif'
                                    fontWeight={600}
                                    fontSize={{ xs: '18px', sm: '20px' }}
                                    lineHeight='28px'
                                    color='#132F5F'
                                >
                                    Verify Indigent Details
                                </Typography>
                            </Stack>
                            
                            {/* Close Button */}
                            <IconButton
                                onClick={handleCancel}
                                sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    color: '#666',
                                    backgroundColor: '#fff',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                        color: '#333',
                                    },
                                    width: 32,
                                    height: 32,
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>

                        {/* Editing Alert */}
                        {isEditing && (
                            <Alert 
                                severity="info" 
                                sx={{ 
                                    borderRadius: '12px',
                                    fontFamily: 'Poppins, sans-serif',
                                    '& .MuiAlert-message': {
                                        fontSize: '14px',
                                    },
                                }}
                            >
                                You are editing the fields.
                            </Alert>
                        )}

                        {/* Form Fields */}
                        <Stack spacing={isMobile ? 2 : 3}>
                            {/* ID Number */}
                            <TextField
                                label="ID Number"
                                value={formData.idNumber}
                                onChange={(e) => handleFieldChange('idNumber', e.target.value)}
                                fullWidth
                                variant="outlined"
                                sx={textFieldSx}
                                disabled={isLoading || isVerifying}
                                required
                            />

                            {/* First Name */}
                            <TextField
                                label="First Name"
                                value={formData.firstName}
                                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                                fullWidth
                                variant="outlined"
                                sx={textFieldSx}
                                disabled={isLoading || isVerifying}
                                required
                            />

                            {/* Surname */}
                            <TextField
                                label="Surname"
                                value={formData.surname}
                                onChange={(e) => handleFieldChange('surname', e.target.value)}
                                fullWidth
                                variant="outlined"
                                sx={textFieldSx}
                                disabled={isLoading || isVerifying}
                                required
                            />

                            {/* Address Line 1 */}
                            <TextField
                                label="Address Line 1"
                                value={formData.addressLine1}
                                onChange={(e) => handleFieldChange('addressLine1', e.target.value)}
                                fullWidth
                                variant="outlined"
                                sx={textFieldSx}
                                disabled={isLoading || isVerifying}
                                required
                            />

                            {/* Address Line 2 */}
                            <TextField
                                label="Address Line 2 (Optional)"
                                value={formData.addressLine2}
                                onChange={(e) => handleFieldChange('addressLine2', e.target.value)}
                                fullWidth
                                variant="outlined"
                                sx={textFieldSx}
                                disabled={isLoading || isVerifying}
                            />

                            {/* Postal Code */}
                            <TextField
                                label="Postal Code"
                                value={formData.postalCode}
                                onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                                fullWidth
                                variant="outlined"
                                sx={textFieldSx}
                                disabled={isLoading || isVerifying}
                                required
                            />
                        </Stack>

                        {/* Action Buttons */}
                        <Stack 
                            direction={isMobile ? 'column' : 'row'} 
                            spacing={2} 
                            justifyContent="center"
                            sx={{ mt: 2 }}
                        >
                            <CancelButton
                                onClick={handleCancel}
                                disabled={isLoading || isVerifying}
                                sx={{
                                    color: '#DE3B40',
                                    borderColor: '#DE3B40',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: { xs: 14, sm: 16 },
                                    height: { xs: 48, sm: 56 },
                                    width: isMobile ? '100%' : 'auto',
                                    minWidth: 120,
                                    px: { xs: 3, sm: 4 },
                                    '&:hover': { 
                                        backgroundColor: '#FFECEC',
                                        borderColor: '#DE3B40',
                                    },
                                    '&:disabled': {
                                        color: '#ccc',
                                        borderColor: '#ccc',
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                            >
                                Cancel
                            </CancelButton>
                            
                            <ActionButton
                                onClick={handleVerify}
                                disabled={isLoading || isVerifying || !isFormValid()}
                                startIcon={
                                    (isLoading || isVerifying) ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        <VerifiedUserIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                                    )
                                }
                                sx={{
                                    backgroundColor: '#132F5F',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: { xs: 14, sm: 16 },
                                    height: { xs: 48, sm: 56 },
                                    width: isMobile ? '100%' : 'auto',
                                    minWidth: 120,
                                    px: { xs: 3, sm: 4 },
                                    '&:hover': { 
                                        backgroundColor: '#0f254d' 
                                    },
                                    '&:disabled': { 
                                        backgroundColor: '#ccc' 
                                    },
                                }}
                            >
                                {(isLoading || isVerifying) ? 'Fetching...' : 'Fetch KYC Data'}
                            </ActionButton>
                        </Stack>
                    </Stack>
                </Box>
            </Modal>

            {/* Verification Results Dialog - After successful verification */}
            <VerificationResultsDialog
                open={showResultsDialog}
                onClose={handleResultsDialogClose}
                onCloseAll={handleCloseAllDialogs}
                verificationData={verificationResults}
                showForceUpdate={false}
            />
        </>
    );
};

export default VerificationDialog;
